import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { LayoutDashboard, Moon, Plus, Sun } from 'lucide-react';
import { navItemsFor } from '@/lib/nav';
import { NAV_ICONS } from '@/lib/nav-icons';
import { useMe } from '@/lib/use-me';
import { useTheme } from '@/lib/theme';
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';

const QUICK_ACTIONS = [
  { label: 'New customer', path: '/customers/new' },
  { label: 'New lead', path: '/leads/new' },
  { label: 'New project', path: '/projects/new' },
  { label: 'New invoice', path: '/invoices/new' },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [, navigate] = useLocation();
  const { data: me } = useMe();
  const { theme, toggle } = useTheme();

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const go = (path: string) => {
    navigate(path);
    setOpen(false);
  };

  const items = me ? navItemsFor(me.role) : [];

  return (
    <CommandDialog open={open} onOpenChange={setOpen} title="Command palette">
      <Command>
      <CommandInput placeholder="Search pages or run a command…" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        <CommandGroup heading="Quick actions">
          {QUICK_ACTIONS.map((a) => (
            <CommandItem key={a.path} onSelect={() => go(a.path)}>
              <Plus className="size-4 text-muted-foreground" />
              {a.label}
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandGroup heading="Navigate">
          {items.map((item) => {
            const Icon = NAV_ICONS[item.path] ?? LayoutDashboard;
            return (
              <CommandItem key={item.path} onSelect={() => go(item.path)}>
                <Icon className="size-4 text-muted-foreground" />
                {item.label}
              </CommandItem>
            );
          })}
        </CommandGroup>

        <CommandGroup heading="Preferences">
          <CommandItem
            onSelect={() => {
              toggle();
              setOpen(false);
            }}
          >
            {theme === 'dark' ? (
              <Sun className="size-4 text-muted-foreground" />
            ) : (
              <Moon className="size-4 text-muted-foreground" />
            )}
            Switch to {theme === 'dark' ? 'light' : 'dark'} mode
          </CommandItem>
        </CommandGroup>
      </CommandList>
      </Command>
    </CommandDialog>
  );
}
